import React, { useState } from "react";
import { Trash2, Plus, Minus, Eye, Download, X } from "lucide-react";
import { PRINT_MODES } from "./PrintEstimator";
import { StlViewer } from "react-stl-viewer";

import "@google/model-viewer";

const SURCHARGE_TAG_CLASS = "rounded border border-gray-200 bg-gray-50 px-2 py-1";

const CartItem = ({ item, onRemove, onUpdateQuantity, editable = true, initialQuantity = 1 }) => {
  const [showPreview, setShowPreview] = useState(false);
  const isStlFile = item.file_name?.toLowerCase().endsWith(".stl") || item.file_name?.toLowerCase().endsWith(".3mf");
  const isGlbFile = item.file_name?.toLowerCase().endsWith(".glb");
  const canPreview = isStlFile || isGlbFile;
  if (!editable) item.quantity = initialQuantity;
  const handlePreview = () => {
    setShowPreview(!showPreview);
  };

  const handleQuantityChange = (newQuantity) => {
    if (!editable) return;
    if (newQuantity < 1) return;
    onUpdateQuantity(item.uuid, newQuantity);
  };

  return (
    <div className="border rounded-lg p-4 mb-4 bg-white shadow-sm hover:shadow-md transition-shadow">
      <div className="flex justify-between items-start mb-1.5">
        <div className="flex flex-wrap items-center">
          <span className="font-semibold text-gray-800 mr-2 break-all">{item.file_name}</span>
          {canPreview && (
            <button onClick={handlePreview} className="text-gray-500 hover:text-blue-500 transition-colors flex items-center" title="미리보기">
              <Eye className="w-4 h-4" /> <span className="text-xs ml-1"> 미리보기</span>
            </button>
          )}
        </div>
        {editable && (
          <button onClick={() => onRemove(item.uuid)} className="text-gray-400 hover:text-red-500 transition-colors p-1 ml-2 flex-shrink-0">
            <Trash2 className="w-5 h-5" />
          </button>
        )}
      </div>
      <div className="grid grid-cols-3 gap-2 mb-3 text-sm">
        <div className="flex items-center bg-gray-50 p-2 rounded whitespace-nowrap">
          <span className="text-gray-500 mr-2">모드:</span>
          <span className="font-medium">{PRINT_MODES.find((mode) => mode.id === item.print_mode)?.label}</span>
        </div>
        <div className="flex items-center bg-gray-50 p-2 rounded whitespace-nowrap">
          <span className="text-gray-500 mr-2">소재:</span>
          <span className="font-medium">{item.material === "pacf" ? "PA-CF" : item.material.toUpperCase()}</span>
        </div>
        <div className="flex items-center bg-gray-50 p-2 rounded whitespace-nowrap">
          <span className="text-gray-500 mr-2">색상:</span>
          {item.color === null ? (
            <div className="w-5 h-5 rounded-full mr-2 border border-gray-200 bg-gradient-to-r from-blue-400 via-purple-400 to-yellow-400 flex items-center justify-center">
              <span className="text-white text-xs font-bold">?</span>
            </div>
          ) : item.color === "clear" ? (
            <div
              className="w-5 h-5 rounded-full border border-gray-200 shadow-inner mr-1"
              style={{
                backgroundImage: 'url("/clear-pattern.svg")',
                backgroundSize: "100%",
                backgroundColor: "#fff",
              }}
            ></div>
          ) : (
            <div className="w-5 h-5 rounded-full border border-gray-200 shadow-inner mr-1" style={{ backgroundColor: item.color }}></div>
          )}
        </div>
      </div>
      {(item.large_model || item.express_delivery) && (
        <div className="mb-3 flex flex-wrap gap-2 text-xs text-gray-600">
          {item.large_model && <span className={SURCHARGE_TAG_CLASS}>대형 모델 할증 +50%</span>}
          {item.express_delivery && <span className={SURCHARGE_TAG_CLASS}>빠른 납기 +50%</span>}
        </div>
      )}
      <div className="flex justify-between items-center pt-2 border-t">
        {editable ? (
          <div className="flex items-center border rounded-lg overflow-hidden shadow-sm">
            <button onClick={() => handleQuantityChange(item.quantity - 1)} className="p-2 text-gray-600 hover:bg-gray-100 transition-colors" disabled={item.quantity <= 1}>
              <Minus className="w-4 h-4" />
            </button>
            <span className="px-4 py-1 font-medium bg-white">{item.quantity}</span>
            <button onClick={() => handleQuantityChange(item.quantity + 1)} className="p-2 text-gray-600 hover:bg-gray-100 transition-colors">
              <Plus className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <div className="flex items-center border rounded-lg overflow-hidden shadow-sm opacity-70">
            <button className="p-2 text-gray-400 cursor-not-allowed" disabled>
              <Minus className="w-4 h-4" />
            </button>
            <span className="px-4 py-1 font-medium bg-white">{item.quantity}</span>
            <button className="p-2 text-gray-400 cursor-not-allowed" disabled>
              <Plus className="w-4 h-4" />
            </button>
          </div>
        )}

        <div className="text-right">
          <div className="text-gray-500 text-sm">
            {item.estimated_cost.toLocaleString()}원 × {item.quantity}
          </div>
          <div className="font-bold text-teal-500">{(item.estimated_cost * item.quantity).toLocaleString()}원</div>
        </div>
      </div>

      {/* Preview Modal */}
      {showPreview && canPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-xl w-full max-h-screen overflow-auto relative">
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-bold">{item.file_name} 미리보기</h3>
                <a href={item.url} download={item.file_name} className="text-gray-500 hover:text-teal-500 transition-colors">
                  <Download className="w-4 h-4" />
                </a>
              </div>
              <button onClick={handlePreview} className="text-gray-500 hover:text-gray-700 p-1">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="h-96 w-full">
              {isStlFile ? (
                <StlViewer
                  url={item.url}
                  orbitControls
                  showAxes
                  style={{ width: "100%", height: "100%" }}
                  floorProps={{
                    gridWidth: 256,
                  }}
                  modelProps={{ color: item.color ? item.color : "grey", rotationX: 0, rotationY: 0, rotationZ: 0 }}
                  cameraProps={{
                    initialPosition: { distance: 0.5 },
                  }}
                />
              ) : isGlbFile ? (
                <model-viewer src={item.url} alt={`${item.file_name} 3D 모델`} auto-rotate camera-controls style={{ width: "100%", height: "100%" }} loading="eager"></model-viewer>
              ) : null}
            </div>

            <div className="grid grid-cols-3 gap-2 text-sm">
              <div className="flex items-center bg-gray-50 p-2.5 rounded whitespace-nowrap">
                <span className="text-gray-500 mr-2">모드:</span>
                <span className="font-medium">{PRINT_MODES.find((mode) => mode.id === item.print_mode)?.label}</span>
              </div>
              <div className="flex items-center bg-gray-50 p-2.5 rounded whitespace-nowrap">
                <span className="text-gray-500 mr-2">소재:</span>
                <span className="font-medium">{item.material === "pacf" ? "PA-CF" : item.material.toUpperCase()}</span>
              </div>
              <div className="flex items-center bg-gray-50 p-2.5 rounded whitespace-nowrap">
                <span className="text-gray-500 mr-2">색상:</span>
                {item.color === null ? (
                  <div className="w-5 h-5 rounded-full mr-2 border border-gray-200 bg-gradient-to-r from-blue-400 via-purple-400 to-yellow-400 flex items-center justify-center">
                    <span className="text-white text-xs font-bold">?</span>
                  </div>
                ) : item.color === "clear" ? (
                  <div
                    className="w-5 h-5 rounded-full border border-gray-200 shadow-inner mr-1"
                    style={{
                      backgroundImage: 'url("/clear-pattern.svg")',
                      backgroundSize: "100%",
                      backgroundColor: "#fff",
                    }}
                  ></div>
                ) : (
                  <div className="w-5 h-5 rounded-full border border-gray-200 shadow-inner mr-1" style={{ backgroundColor: item.color }}></div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CartItem;
